import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'da', label: 'Dansk', short: 'DA' },
  { code: 'de', label: 'Deutsch', short: 'DE' },
];

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const current = languages.find((l) => l.code === i18n.language) || languages[0];

  const change = (code: string) => {
    i18n.changeLanguage(code);
    document.documentElement.lang = code;
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300/70 bg-background-50 px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100 cursor-pointer"
        aria-label="Change language"
      >
        <span className="flex h-4 w-4 items-center justify-center text-foreground-700">
          <i className="ri-global-line text-sm"></i>
        </span>
        <span>{current.short}</span>
        <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
          <i className={`ri-arrow-down-s-line text-sm transition-transform ${open ? 'rotate-180' : ''}`}></i>
        </span>
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-40 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-1">
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => change(lang.code)}
              className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm whitespace-nowrap transition-colors cursor-pointer ${
                current.code === lang.code
                  ? 'bg-primary-100 text-primary-800 font-semibold'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              <span>{lang.label}</span>
              <span className="text-xs text-foreground-400">{lang.short}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}